import { Button, Text } from '@lmc/ui-kit/src/general'
import { Modal } from 'antd'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const DATE_FORMAT = 'DD-MM-YY, HH:mm'
const MODAL_WIDTH = 647

const StyledModal = styled(Modal)`
  /* The design has dividers above and below the body, inset 24px from the
     modal edges, and a 24px sentence-case heading. antd v5 pads
     .ant-modal-content as a whole, so sections carry their own spacing:
     vertical padding plus the inset as horizontal margin. The app-wide
     antdModal.less strips the header border, and the ui-kit legacy Modal.less
     uppercases titles at 28px/400 and kills the footer border — the
     !importants here out-rank the legacy ones. */
  .ant-modal-content {
    padding: 0;
  }

  .ant-modal-header {
    border-bottom: 1px solid ${({ theme }) => theme.color.neutral.color8};
    /* antdModal.less rounds the header 8px !important, which bends the
       divider's ends. */
    border-radius: 0 !important;
    margin: 0 24px;
    padding: 16px 0 !important;
  }

  .ant-modal-body {
    padding: 0 24px;
  }

  .ant-modal-footer {
    border-top: 1px solid ${({ theme }) => theme.color.neutral.color8} !important;
    margin: 0 24px;
    padding: 16px 0;
  }

  .ant-modal-title {
    font-size: 24px;
    font-weight: ${({ theme }) => theme.font.weight.medium} !important;
    text-transform: none;
  }
`

export type RemoveBodyMapModalProps = {
  bodyMap: {
    createdOn: string
    id: string
    lastActivityOn: string | null
    title: string | null
  }
  isOpen: boolean
  onCancel: () => void
  onConfirm: () => void
  testId?: string
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
`

const Subject = styled.div`
  display: flex;
  flex-direction: column;
`

const Footer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

/**
 * Bespoke rather than ui-kit's ConfirmationModal because it names the body map
 * and when it was last updated — removing the wrong one is easy when a case
 * links several.
 */
const RemoveBodyMapModal = ({
  bodyMap,
  isOpen,
  onCancel,
  onConfirm,
  testId
}: RemoveBodyMapModalProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  const dateLabel = bodyMap.lastActivityOn
    ? `${t(incidentReportsConstants.LAST_UPDATED_ON)} ${dayjs(bodyMap.lastActivityOn).format(DATE_FORMAT)}`
    : `${t(incidentReportsConstants.ADDED_ON)} ${dayjs(bodyMap.createdOn).format(DATE_FORMAT)}`

  return (
    <StyledModal
      data-testid={testId}
      destroyOnClose
      footer={
        <Footer>
          <Button
            data-testid={testId && `${testId}-confirm`}
            onClick={onConfirm}
            variant="danger"
          >
            {t(incidentReportsConstants.REMOVE)}
          </Button>
          <Button
            data-testid={testId && `${testId}-cancel`}
            onClick={onCancel}
            variant="default"
          >
            {t(incidentReportsConstants.CANCEL)}
          </Button>
        </Footer>
      }
      onCancel={onCancel}
      open={isOpen}
      /* A plain string, so the 24px .ant-modal-title styling applies — a Text
         wrapper would pin it back to 16px. */
      title={t(incidentReportsConstants.REMOVING_BODY_MAP)}
      width={MODAL_WIDTH}
    >
      <Body>
        <Text level={1}>
          {t(incidentReportsConstants.YOU_ARE_ABOUT_TO_REMOVE)}
        </Text>
        <Subject>
          <Text level={1} weight="medium">
            {bodyMap.title}
          </Text>
          <Text level={1}>{dateLabel}</Text>
        </Subject>
        <Text level={1} weight="medium">
          {t(incidentReportsConstants.ARE_YOU_SURE_CONTINUE)}
        </Text>
      </Body>
    </StyledModal>
  )
}

export default RemoveBodyMapModal
